import { createRoot } from 'react-dom/client';

// zercode packages
import { consumeAuthHandoff, useSessionStore } from '@zercode/auth';
import { fetchTenantBranding } from '@zercode/tenancy';
import { resolveBrandingBeforePaint } from '@zercode/theme';

// project imports
import App from './App';
import { useBrandingStore } from './lib/brandingStore';
import { authApi, controlPlaneClient, tenant } from './lib/zercode';

// style + assets (Berry seed)
import 'berry/assets/scss/style.scss';

// Self-hosted webfonts — every family offered by FONT_OPTIONS (@zercode/theme) must be
// imported here, or picking it in the Theme Customization drawer silently falls back to
// sans-serif. All of these carry latin-ext, i.e. the Turkish ğ/ş/ı glyphs. The woff2 files
// are only fetched by the browser for the family actually in use, so the unselected ones
// cost @font-face rules and nothing more.
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/jost/400.css';
import '@fontsource/jost/500.css';
import '@fontsource/jost/600.css';
import '@fontsource/jost/700.css';
import '@fontsource/nunito-sans/400.css';
import '@fontsource/nunito-sans/500.css';
import '@fontsource/nunito-sans/600.css';
import '@fontsource/nunito-sans/700.css';
// Questrial and Didact Gothic ship ONE weight (400) — the 500/600/700 in Berry's
// typography scale are synthesized by the browser (faux bold).
import '@fontsource/questrial/400.css';
import '@fontsource/didact-gothic/400.css';

// ==============================|| BOOTSTRAP ||============================== //

async function bootstrap() {
  // Tenant theme resolves BEFORE first paint [STD-FE]: fetch branding from the
  // Control Plane, THEN render. Until then index.html shows a neutral splash,
  // so there is no branding flash. On failure a neutral fallback is used.
  const branding = await resolveBrandingBeforePaint(() => {
    if (!tenant.slug) {
      return Promise.reject(new Error(`tenant could not be resolved from host '${tenant.hostname}' (source: ${tenant.source})`));
    }
    return fetchTenantBranding(controlPlaneClient, tenant.slug);
  });

  // seed the live branding store BEFORE render — applies favicon/title, injects
  // the tenant font <link> and pre-sets the default color scheme attribute so
  // the very first paint is fully tenant-branded (no font/scheme flash) [STD-FE]
  useBrandingStore.getState().setBranding(branding);

  // FE-0/FE-2: silent session restore from the httpOnly refresh cookie (BE-1) BEFORE first paint.
  // Cookie present -> memory session hydrated (refresh sets tokens, /me flips status to
  // 'authenticated'); absent/expired/error -> guest. Branding already resolved above, so the neutral
  // splash covers this extra round-trip and the first paint is the correct auth-or-guest state — no
  // login flash. Tokens stay memory-only [STD-FE].
  // AUTH-HANDOFF: a sign-in that happened on a DIFFERENT SITE (a tenant's own branded login
  // domain) cannot leave a usable refresh cookie here — the browser treats it as a third-party
  // write. The login app therefore carries the token pair over in the URL fragment; this strips it
  // and seeds the store, so the refresh below has a body token to send. That call is same-site with
  // Identity, so the cookie it sets in response IS first-party and every later load restores the
  // ordinary way. No-op on a same-site sign-in, which never carries a fragment.
  consumeAuthHandoff();

  try {
    if (await authApi.refresh()) {
      await authApi.me();
    } else {
      useSessionStore.getState().clearSession();
    }
  } catch {
    useSessionStore.getState().clearSession(); // never block the app on a restore failure
  }

  const container = document.getElementById('root');
  createRoot(container!).render(<App />);
}

void bootstrap();
